/**
 * The exports two procedures have always told operators to run "below".
 *
 * "Export that group's recovery bundle below" and "Export every group's bundle
 * below" were steps two of the restore and rotate procedures, and there was
 * nothing below either of them. Both commands take a file the panel had no way
 * to produce, so both procedures stopped at their second line.
 *
 * **A bundle contains no secret.** Every key inside is already wrapped to the
 * recovery public key, which only the shares open — so it can be downloaded,
 * e-mailed, or carried on a stick, and the copy says so. An operator who
 * believes they are handling key material will handle it once and then avoid
 * the procedure, which is worse than either outcome.
 *
 * Downloaded rather than shown. These are inputs to a command-line tool, and a
 * JSON blob on screen invites somebody to select it, miss a character, and
 * discover the truncation somewhere much less forgiving.
 */

import { useState } from "react";
import { errorMessage, trpcClient } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { useToast } from "../../components/ui/Toast.js";

function download(name: string, data: unknown): void {
  const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** One group's bundle — the input to `fc-recovery open-group --bundle`. */
export function ExportGroupBundle({ groupId }: { groupId: string }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <Button
      variant="secondary"
      loading={busy}
      onClick={() => {
        setBusy(true);
        void trpcClient.admin.groupBundle
          .query({ groupId })
          .then((bundle) => {
            download(`bundle-${groupId}.json`, bundle);
            toast("Bundle downloaded. It contains no secret.");
          })
          .catch((caught: unknown) =>
            toast(errorMessage(caught), { variant: "danger" }),
          )
          .finally(() => setBusy(false));
      }}
    >
      Export recovery bundle
    </Button>
  );
}

/** Every group at once — the input to `fc-recovery rotate --bundles`. */
export function ExportAllBundles() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  return (
    <Button
      variant="secondary"
      loading={busy}
      onClick={() => {
        setBusy(true);
        void trpcClient.admin.allGroupBundles
          .query()
          .then((bundles) => {
            download("all-groups.json", bundles);
            /*
             * The count matters here in a way it does not for one group:
             * rotation has to cover everything, and a number lower than the
             * panel's "groups covered" is the sign that it will not.
             */
            toast(
              `${String(bundles.length)} ${
                bundles.length === 1 ? "bundle" : "bundles"
              } downloaded. Check that against "Groups covered" above.`,
            );
          })
          .catch((caught: unknown) =>
            toast(errorMessage(caught), { variant: "danger" }),
          )
          .finally(() => setBusy(false));
      }}
    >
      Export every group&rsquo;s bundle
    </Button>
  );
}
